import { useState, useEffect } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  BarChart3,
  Key,
  Heart,
  Star,
  TrendingUp,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  Gamepad2,
  Users,
  Activity,
  Award,
  BarChart,
  LineChart,
  PieChart
} from "lucide-react";
import {
  Tooltip as UITooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { Info } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart as RechartsLineChart,
  Line,
  AreaChart,
  Area,
  Legend
} from "recharts";

interface DeveloperStats {
  totalGames: number;
  totalKeysDistributed: number;
  totalKeyRequests: number;
  approvedKeyRequests: number;
  rejectedKeyRequests: number;
  pendingKeyRequests: number;
  totalLikes: number;
  totalFavorites: number;
  totalPlaytime: number; // en minutes
  averagePlaytimePerGame: number;
  keyRequestsByMonth: Array<{ month: string; approved: number; rejected: number; pending: number; total: number }>;
  likesByMonth: Array<{ month: string; count: number }>;
  favoritesByMonth: Array<{ month: string; count: number }>;
  keysDistributedByMonth: Array<{ month: string; count: number }>;
  playtimeByMonth: Array<{ month: string; minutes: number; hours: number }>;
  gamesStats: Array<{
    id: string;
    title: string;
    keyRequests: number;
    approvedRequests: number;
    rejectedRequests: number;
    pendingRequests: number;
    likes: number;
    favorites: number;
    playtime: number;
    keysDistributed: number;
    availableKeys: number;
    totalKeys: number;
  }>;
  approvalRate: number;
  averageResponseTime: number; // en heures
  // Raw data for filtering
  rawData?: {
    keyRequests: any[];
    likes: any[];
    favorites: any[];
    distributedKeys: any[];
    playtimeData: any[];
    startDate: Date;
  };
}

const COLORS = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#ef4444'];

export default function DeveloperStats() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DeveloperStats | null>(null);
  const [dateRange, setDateRange] = useState("1year");
  const [selectedGame, setSelectedGame] = useState<string>("all");
  const [isPremium, setIsPremium] = useState(false);
  const [checkingPremium, setCheckingPremium] = useState(true);
  const [platformStats, setPlatformStats] = useState<any>(null);
  const [genreFilter, setGenreFilter] = useState("all");

  // Vérifier le statut premium
  useEffect(() => {
    const checkPremiumStatus = async () => {
      if (!user?.id) {
        setCheckingPremium(false);
        return;
      }

      try {
        const { data: profile, error } = await supabase
          .from("profiles")
          .select("is_premium, premium_end_date, user_type")
          .eq("id", user.id)
          .single();

        if (error) {
          console.error("Error checking premium status:", error);
          setIsPremium(false);
          setCheckingPremium(false);
          return;
        }

        // Vérifier que c'est un développeur
        if (profile?.user_type !== 'developer') {
          setIsPremium(false);
          setCheckingPremium(false);
          return;
        }

        // Vérifier le statut premium
        const hasPremium = profile?.is_premium === true;
        const premiumEndDate = profile?.premium_end_date;

        if (hasPremium) {
          // Si pas de date de fin, c'est premium à vie
          if (!premiumEndDate) {
            setIsPremium(true);
          } else {
            // Vérifier que la date n'est pas expirée
            const endDate = new Date(premiumEndDate);
            const now = new Date();
            setIsPremium(endDate > now);
          }
        } else {
          setIsPremium(false);
        }
      } catch (error) {
        console.error("Error checking premium:", error);
        setIsPremium(false);
      } finally {
        setCheckingPremium(false);
      }
    };

    checkPremiumStatus();
  }, [user?.id]);

  useEffect(() => {
    if (user?.id && isPremium) {
      fetchStats();
      fetchPlatformStats();
    } else if (!isPremium && !checkingPremium) {
      setLoading(false);
    }
  }, [user?.id, dateRange, isPremium, checkingPremium]);

  const fetchPlatformStats = async () => {
    try {
      const { data, error } = await supabase
        .from("platform_stats")
        .select("*");

      if (error) throw error;

      const statsMap = data.reduce((acc: any, item: any) => {
        acc[item.category] = item.data;
        return acc;
      }, {});

      setPlatformStats(statsMap);
    } catch (error) {
      console.error("Error fetching platform stats:", error);
    }
  };

  const fetchStats = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);

      // Calculate date range
      const now = new Date();
      let startDate: Date;

      switch (dateRange) {
        case "1month":
          startDate = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
          break;
        case "3months":
          startDate = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
          break;
        case "6months":
          startDate = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
          break;
        case "1year":
          startDate = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
          break;
        default:
          startDate = new Date(0); // All time
      }

      // Fetch all games for this developer
      const { data: gamesData, error: gamesError } = await supabase
        .from("games")
        .select("id, title, created_at, total_keys, available_keys")
        .eq("developer_id", user.id);

      if (gamesError) throw gamesError;

      const gameIds = (gamesData || []).map(g => g.id);

      if (gameIds.length === 0) {
        setStats({
          totalGames: 0,
          totalKeysDistributed: 0,
          totalKeyRequests: 0,
          approvedKeyRequests: 0,
          rejectedKeyRequests: 0,
          pendingKeyRequests: 0,
          totalLikes: 0,
          totalFavorites: 0,
          totalPlaytime: 0,
          averagePlaytimePerGame: 0,
          keyRequestsByMonth: [],
          likesByMonth: [],
          favoritesByMonth: [],
          keysDistributedByMonth: [],
          playtimeByMonth: [],
          gamesStats: [],
          approvalRate: 0,
          averageResponseTime: 0
        });
        return;
      }

      // Fetch all related data
      const [
        keyRequestsResult,
        likesResult,
        favoritesResult,
        keysResult,
        playtimeResult
      ] = await Promise.all([
        supabase
          .from("key_requests")
          .select("id, game_id, status, created_at, updated_at")
          .in("game_id", gameIds)
          .gte("created_at", startDate.toISOString()),
        supabase
          .from("game_likes")
          .select("id, game_id, created_at")
          .in("game_id", gameIds)
          .gte("created_at", startDate.toISOString()),
        supabase
          .from("game_favorites")
          .select("id, game_id, created_at")
          .in("game_id", gameIds)
          .gte("created_at", startDate.toISOString()),
        supabase
          .from("keys")
          .select("id, game_id, is_claimed, created_at")
          .in("game_id", gameIds)
          .eq("is_claimed", true)
          .gte("created_at", startDate.toISOString()),
        supabase
          .from("playtime_tracking")
          .select("game_id, steam_playtime, created_at")
          .in("game_id", gameIds)
          .gte("created_at", startDate.toISOString())
      ]);

      if (keyRequestsResult.error) throw keyRequestsResult.error;
      if (likesResult.error) throw likesResult.error;
      if (favoritesResult.error) throw favoritesResult.error;
      if (keysResult.error) throw keysResult.error;
      if (playtimeResult.error) throw playtimeResult.error;

      const keyRequests = keyRequestsResult.data || [];
      const likes = likesResult.data || [];
      const favorites = favoritesResult.data || [];
      const distributedKeys = keysResult.data || [];
      const playtimeData = playtimeResult.data || [];

      // Calculate basic stats
      const totalGames = gamesData?.length || 0;
      const totalKeysDistributed = distributedKeys.length;
      const totalKeyRequests = keyRequests.length;
      const approvedKeyRequests = keyRequests.filter(k => k.status === "approved").length;
      const rejectedKeyRequests = keyRequests.filter(k => k.status === "rejected").length;
      const pendingKeyRequests = keyRequests.filter(k => k.status === "pending").length;
      const totalLikes = likes.length;
      const totalFavorites = favorites.length;
      const totalPlaytime = playtimeData.reduce((sum, p) => sum + (p.steam_playtime || 0), 0);
      const averagePlaytimePerGame = totalGames > 0 ? totalPlaytime / totalGames : 0;

      // Calculate approval rate
      const processedRequests = approvedKeyRequests + rejectedKeyRequests;
      const approvalRate = processedRequests > 0 ? (approvedKeyRequests / processedRequests) * 100 : 0;

      // Calculate average response time (only for processed requests)
      const processedRequestsWithTime = keyRequests
        .filter(k => k.status !== "pending" && k.updated_at)
        .map(k => {
          const created = new Date(k.created_at).getTime();
          const updated = new Date(k.updated_at).getTime();
          return (updated - created) / (1000 * 60 * 60); // Convert to hours
        });
      const averageResponseTime = processedRequestsWithTime.length > 0
        ? processedRequestsWithTime.reduce((a, b) => a + b, 0) / processedRequestsWithTime.length
        : 0;

      // Generate monthly data
      const keyRequestsByMonth = generateKeyRequestsMonthlyData(keyRequests, startDate);
      const likesByMonth = generateMonthlyData(likes, startDate);
      const favoritesByMonth = generateMonthlyData(favorites, startDate);
      const keysDistributedByMonth = generateMonthlyData(distributedKeys, startDate);
      const playtimeByMonth = generatePlaytimeMonthlyData(playtimeData, startDate);

      // Calculate stats per game
      const gamesStats = (gamesData || []).map(game => {
        const gameKeyRequests = keyRequests.filter(k => k.game_id === game.id);
        const gameLikes = likes.filter(l => l.game_id === game.id);
        const gameFavorites = favorites.filter(f => f.game_id === game.id);
        const gameKeys = distributedKeys.filter(k => k.game_id === game.id);
        const gamePlaytime = playtimeData
          .filter(p => p.game_id === game.id)
          .reduce((sum, p) => sum + (p.steam_playtime || 0), 0);

        return {
          id: game.id,
          title: game.title,
          keyRequests: gameKeyRequests.length,
          approvedRequests: gameKeyRequests.filter(k => k.status === "approved").length,
          rejectedRequests: gameKeyRequests.filter(k => k.status === "rejected").length,
          pendingRequests: gameKeyRequests.filter(k => k.status === "pending").length,
          likes: gameLikes.length,
          favorites: gameFavorites.length,
          playtime: gamePlaytime,
          keysDistributed: gameKeys.length,
          availableKeys: game.available_keys || 0,
          totalKeys: game.total_keys || 0
        };
      });

      setStats({
        totalGames,
        totalKeysDistributed,
        totalKeyRequests,
        approvedKeyRequests,
        rejectedKeyRequests,
        pendingKeyRequests,
        totalLikes,
        totalFavorites,
        totalPlaytime,
        averagePlaytimePerGame,
        keyRequestsByMonth,
        likesByMonth,
        favoritesByMonth,
        keysDistributedByMonth,
        playtimeByMonth,
        gamesStats,
        approvalRate,
        averageResponseTime,
        rawData: {
          keyRequests,
          likes,
          favorites,
          distributedKeys,
          playtimeData,
          startDate: new Date(startDate)
        }
      });
    } catch (error) {
      console.error("Error fetching stats:", error);
      toast.error("Error loading statistics");
    } finally {
      setLoading(false);
    }
  };

  const generateMonthlyData = (data: any[], startDate: Date) => {
    const months: { [key: string]: number } = {};
    const currentDate = new Date();

    for (let d = new Date(startDate); d <= currentDate; d.setMonth(d.getMonth() + 1)) {
      const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      months[monthKey] = 0;
    }

    data.forEach(item => {
      const date = new Date(item.created_at);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      if (months.hasOwnProperty(monthKey)) {
        months[monthKey]++;
      }
    });

    return Object.entries(months).map(([month, count]) => ({
      month: new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
      count
    }));
  };

  const generateKeyRequestsMonthlyData = (data: any[], startDate: Date) => {
    const months: { [key: string]: { approved: number; rejected: number; pending: number } } = {};
    const currentDate = new Date();

    for (let d = new Date(startDate); d <= currentDate; d.setMonth(d.getMonth() + 1)) {
      const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      months[monthKey] = { approved: 0, rejected: 0, pending: 0 };
    }

    data.forEach(item => {
      const date = new Date(item.created_at);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      if (months.hasOwnProperty(monthKey)) {
        months[monthKey][item.status as keyof typeof months[typeof monthKey]]++;
      }
    });

    return Object.entries(months).map(([month, counts]) => ({
      month: new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
      ...counts,
      total: counts.approved + counts.rejected + counts.pending
    }));
  };

  const generatePlaytimeMonthlyData = (data: any[], startDate: Date) => {
    const months: { [key: string]: number } = {};
    const currentDate = new Date();

    for (let d = new Date(startDate); d <= currentDate; d.setMonth(d.getMonth() + 1)) {
      const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      months[monthKey] = 0;
    }

    data.forEach(item => {
      const date = new Date(item.created_at);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      if (months.hasOwnProperty(monthKey)) {
        months[monthKey] += item.steam_playtime || 0;
      }
    });

    return Object.entries(months).map(([month, minutes]) => ({
      month: new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
      minutes,
      hours: Math.round(minutes / 60)
    }));
  };

  const formatPlaytime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-2 text-muted-foreground">Loading statistics...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!stats) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <p className="text-muted-foreground">No statistics available</p>
          </div>
        </div>
      </Layout>
    );
  }

  // Filter stats by selected game
  const getFilteredStats = () => {
    if (!stats) return null;

    if (selectedGame === "all") {
      return stats;
    }

    // Filter all data for the selected game
    const game = stats.gamesStats.find(g => g.id === selectedGame);
    if (!game || !stats.rawData) return stats;

    // Filter raw data by game
    const filteredKeyRequests = stats.rawData.keyRequests.filter(k => k.game_id === selectedGame);
    const filteredLikes = stats.rawData.likes.filter(l => l.game_id === selectedGame);
    const filteredFavorites = stats.rawData.favorites.filter(f => f.game_id === selectedGame);
    const filteredKeys = stats.rawData.distributedKeys.filter(k => k.game_id === selectedGame);
    const filteredPlaytime = stats.rawData.playtimeData.filter(p => p.game_id === selectedGame);

    // Recalculate monthly data with filtered data
    const filteredKeyRequestsByMonth = generateKeyRequestsMonthlyData(filteredKeyRequests, stats.rawData.startDate);
    const filteredLikesByMonth = generateMonthlyData(filteredLikes, stats.rawData.startDate);
    const filteredFavoritesByMonth = generateMonthlyData(filteredFavorites, stats.rawData.startDate);
    const filteredKeysDistributedByMonth = generateMonthlyData(filteredKeys, stats.rawData.startDate);
    const filteredPlaytimeByMonth = generatePlaytimeMonthlyData(filteredPlaytime, stats.rawData.startDate);

    return {
      ...stats,
      totalGames: 1,
      totalKeysDistributed: game.keysDistributed,
      totalKeyRequests: game.keyRequests,
      approvedKeyRequests: game.approvedRequests,
      rejectedKeyRequests: game.rejectedRequests,
      pendingKeyRequests: game.pendingRequests,
      totalLikes: game.likes,
      totalFavorites: game.favorites,
      totalPlaytime: game.playtime,
      averagePlaytimePerGame: game.playtime,
      keyRequestsByMonth: filteredKeyRequestsByMonth,
      likesByMonth: filteredLikesByMonth,
      favoritesByMonth: filteredFavoritesByMonth,
      keysDistributedByMonth: filteredKeysDistributedByMonth,
      playtimeByMonth: filteredPlaytimeByMonth,
      gamesStats: [game],
      approvalRate: (game.approvedRequests + game.rejectedRequests) > 0
        ? (game.approvedRequests / (game.approvedRequests + game.rejectedRequests)) * 100
        : 0,
      averageResponseTime: stats.averageResponseTime
    };
  };

  const filteredStats = getFilteredStats();
  if (!filteredStats) return null;

  const filteredGamesStats = filteredStats.gamesStats;

  // Prepare data for game comparison chart
  const gameComparisonData = filteredGamesStats.map(game => ({
    name: game.title.length > 15 ? game.title.substring(0, 15) + '...' : game.title,
    fullName: game.title,
    likes: game.likes,
    favorites: game.favorites,
    keyRequests: game.keyRequests,
    playtime: Math.round(game.playtime / 60) // Convert to hours
  }));

  // Afficher un message si pas premium
  if (!checkingPremium && !isPremium) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <Card className="p-8 text-center">
            <BarChart3 className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <h1 className="text-2xl font-bold mb-2">Premium Feature</h1>
            <p className="text-muted-foreground mb-4">
              Statistics are only available for premium developers.
            </p>
            <Button onClick={() => window.location.href = '/subscriptions'}>
              View Subscriptions
            </Button>
          </Card>
        </div>
      </Layout>
    );
  }

  if (checkingPremium || loading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <TooltipProvider>
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold">Statistics</h1>
              <p className="text-muted-foreground">Detailed analytics of your games</p>
            </div>
            <div className="flex gap-4">
              <Select value={selectedGame} onValueChange={setSelectedGame}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Filter by game" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Games</SelectItem>
                  {stats.gamesStats.map(game => (
                    <SelectItem key={game.id} value={game.id}>{game.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={dateRange} onValueChange={setDateRange}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1month">1 Month</SelectItem>
                  <SelectItem value="3months">3 Months</SelectItem>
                  <SelectItem value="6months">6 Months</SelectItem>
                  <SelectItem value="1year">1 Year</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Games</CardTitle>
                <Gamepad2 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{filteredStats.totalGames}</div>
                <p className="text-xs text-muted-foreground">Published games</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Keys Distributed</CardTitle>
                <Key className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{filteredStats.totalKeysDistributed}</div>
                <p className="text-xs text-muted-foreground">Keys claimed</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Key Requests</CardTitle>
                <Key className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{filteredStats.totalKeyRequests}</div>
                <div className="flex gap-2 mt-1">
                  <Badge variant="default" className="text-xs">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    {filteredStats.approvedKeyRequests}
                  </Badge>
                  <Badge variant="destructive" className="text-xs">
                    <XCircle className="w-3 h-3 mr-1" />
                    {filteredStats.rejectedKeyRequests}
                  </Badge>
                  <Badge variant="secondary" className="text-xs">
                    <Clock className="w-3 h-3 mr-1" />
                    {filteredStats.pendingKeyRequests}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Approval Rate</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{filteredStats.approvalRate.toFixed(1)}%</div>
                <p className="text-xs text-muted-foreground">
                  Avg response: {filteredStats.averageResponseTime.toFixed(1)}h
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Engagement Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Likes</CardTitle>
                <Heart className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{filteredStats.totalLikes}</div>
                <p className="text-xs text-muted-foreground">Total likes received</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Favorites</CardTitle>
                <Star className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{filteredStats.totalFavorites}</div>
                <p className="text-xs text-muted-foreground">Games favorited</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Playtime</CardTitle>
                <Activity className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatPlaytime(filteredStats.totalPlaytime)}</div>
                <p className="text-xs text-muted-foreground">
                  Average: {formatPlaytime(filteredStats.averagePlaytimePerGame)}/game
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Engagement</CardTitle>
                <Award className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {filteredStats.totalGames > 0
                    ? ((filteredStats.totalLikes + filteredStats.totalFavorites) / filteredStats.totalGames).toFixed(1)
                    : 0}
                </div>
                <p className="text-xs text-muted-foreground">Likes + Favorites per game</p>
              </CardContent>
            </Card>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  Key Requests by Month
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={filteredStats.keyRequestsByMonth.slice(-12)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Area type="monotone" dataKey="approved" stackId="1" stroke="#22c55e" fill="#22c55e" fillOpacity={0.6} name="Approved" />
                      <Area type="monotone" dataKey="rejected" stackId="1" stroke="#ef4444" fill="#ef4444" fillOpacity={0.6} name="Rejected" />
                      <Area type="monotone" dataKey="pending" stackId="1" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.6} name="Pending" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Engagement by Month
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsLineChart data={filteredStats.likesByMonth.slice(-12)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="count" stroke="#ec4899" strokeWidth={2} name="Likes" />
                    </RechartsLineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Star className="w-5 h-5" />
                  Favorites by Month
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsLineChart data={filteredStats.favoritesByMonth.slice(-12)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Line type="monotone" dataKey="count" stroke="#f59e0b" strokeWidth={2} />
                    </RechartsLineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Key className="w-5 h-5" />
                  Keys Distributed by Month
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsBarChart data={filteredStats.keysDistributedByMonth.slice(-12)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="count" fill="hsl(var(--primary))" />
                    </RechartsBarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5" />
                  Playtime by Month
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsBarChart data={filteredStats.playtimeByMonth.slice(-12)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip formatter={(value) => `${value}h`} />
                      <Bar dataKey="hours" fill="#10b981" />
                    </RechartsBarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart className="w-5 h-5" />
                  Game Comparison
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsBarChart data={gameComparisonData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="likes" fill="#ec4899" name="Likes" />
                      <Bar dataKey="favorites" fill="#f59e0b" name="Favorites" />
                      <Bar dataKey="keyRequests" fill="#3b82f6" name="Requests" />
                    </RechartsBarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Favorite Genre Ranking & Matching */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <CardTitle className="flex items-center gap-2">
                  <PieChart className="w-5 h-5" />
                  Favorite Genre Ranking
                </CardTitle>
                <Select value={genreFilter} onValueChange={setGenreFilter}>
                  <SelectTrigger className="w-32 h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    <SelectItem value="player">Players</SelectItem>
                    <SelectItem value="creator">Creators</SelectItem>
                  </SelectContent>
                </Select>
              </CardHeader>
              <CardContent>
                <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
                  {platformStats?.genre_ranking?.[genreFilter]?.map((item: any, index: number) => (
                    <div key={item.name} className="flex items-center gap-4">
                      <div className="w-8 text-sm font-bold text-muted-foreground">#{index + 1}</div>
                      <div className="flex-1">
                        <div className="flex justify-between mb-1">
                          <span className="text-sm font-medium">{item.name}</span>
                          <span className="text-sm text-muted-foreground">{item.count} ({item.percentage}%)</span>
                        </div>
                        <div className="w-full bg-secondary/20 rounded-full h-1 overflow-hidden">
                          <div
                            className="bg-primary/80 h-full transition-all duration-500 ease-out"
                            style={{ width: `${item.percentage}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  {!platformStats?.genre_ranking && (
                    <div className="text-center py-8 text-muted-foreground">
                      No genre data available. Data is updated nightly.
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Users Matching Your Games
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
                  {platformStats?.game_matches?.games
                    ?.filter((g: any) =>
                      g.developerId === user?.id &&
                      (selectedGame === "all" || g.gameId === selectedGame)
                    )
                    .map((game: any) => (
                      <div key={game.gameId} className="p-3 border rounded-lg bg-card/30">
                        <div className="flex justify-between items-start mb-1.5">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-sm truncate pr-2" title={game.title}>
                              {game.title}
                            </h4>
                            <div className="flex flex-wrap gap-x-2 gap-y-1 mt-1 bg-background/40 rounded-lg !p-3 !my-2">
                              {game.genres?.map((genreName: string) => {
                                const globalRank = platformStats?.genre_ranking?.all?.findIndex(
                                  (item: any) => item.name === genreName
                                );
                                return (
                                  <span key={genreName} className="text-[10px] text-muted-foreground whitespace-nowrap">
                                    {genreName}
                                    {globalRank !== -1 && (
                                      <span className="ml-0.5 text-primary/70 font-medium">
                                        (#{globalRank + 1})
                                      </span>
                                    )}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-2 mt-1">
                          <p className="text-[10px] text-muted-foreground leading-tight flex-1">
                            <span className="text-primary/80 font-medium">{game.playerMatchPercentage}%</span> of players & <span className="text-primary/80 font-medium">{game.creatorMatchPercentage}%</span> of creators could love this game
                          </p>
                          <div className="flex gap-2 items-center shrink-0">
                            <span className="text-[9px] text-muted-foreground font-medium uppercase tracking-wider whitespace-nowrap">
                              Estimated Interest
                            </span>
                            <UITooltip>
                              <TooltipTrigger asChild>
                                <Badge className="bg-primary/20 text-white border-primary/30 px-2 py-0.5 text-xs font-bold cursor-help flex items-center gap-1">
                                  {Math.round(game.interestScore)}/100
                                  <Info className="w-3 h-3" />
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="text-xs">Estimated interest score (out of 100) based on genre popularity and market reach</p>
                              </TooltipContent>
                            </UITooltip>
                          </div>
                        </div>
                      </div>
                    ))}
                  {(platformStats?.game_matches?.games?.filter((g: any) => g.developerId === user?.id).length === 0) && (
                    <div className="text-center py-8 text-muted-foreground">
                      No matching data found for your games.
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Games Stats Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Gamepad2 className="w-5 h-5" />
                Statistics by Game
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2">Game</th>
                      <th className="text-right p-2">Requests</th>
                      <th className="text-right p-2">Approved</th>
                      <th className="text-right p-2">Rejected</th>
                      <th className="text-right p-2">Pending</th>
                      <th className="text-right p-2">Likes</th>
                      <th className="text-right p-2">Favorites</th>
                      <th className="text-right p-2">Playtime</th>
                      <th className="text-right p-2">Keys Distributed</th>
                      <th className="text-right p-2">Keys Available</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStats.gamesStats.map((game) => (
                      <tr key={game.id} className="border-b hover:bg-muted/50">
                        <td className="p-2 font-medium">{game.title}</td>
                        <td className="text-right p-2">{game.keyRequests}</td>
                        <td className="text-right p-2">
                          <Badge variant="default" className="text-xs">{game.approvedRequests}</Badge>
                        </td>
                        <td className="text-right p-2">
                          <Badge variant="destructive" className="text-xs">{game.rejectedRequests}</Badge>
                        </td>
                        <td className="text-right p-2">
                          <Badge variant="secondary" className="text-xs">{game.pendingRequests}</Badge>
                        </td>
                        <td className="text-right p-2">{game.likes}</td>
                        <td className="text-right p-2">{game.favorites}</td>
                        <td className="text-right p-2">{formatPlaytime(game.playtime)}</td>
                        <td className="text-right p-2">{game.keysDistributed}</td>
                        <td className="text-right p-2">
                          {game.availableKeys} / {game.totalKeys}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      </Layout >
    </TooltipProvider>
  );
}
